import { useAccount, usePublicClient, useWalletClient } from "wagmi"
import {
  DOPPLER_V4_ADDRESSES,
  ReadQuoter,
  universalRouterAbi,
} from "doppler-v4-sdk"
import { maxUint256 } from "viem"
import {
  CommandBuilder,
  CommandType,
  V4ActionBuilder,
  V4ActionType,
} from "doppler-router"
import { signPermit } from "doppler-router/dist/Permit2"
import { CHAIN_ID, DOPPLER_V3_PERMIT2_ADDRESS, permit2Abi } from "./constants"
import { usePoolKey } from "./usePoolKey"
import { getDrift } from "@/lib/utils/coins"
import { toast } from "sonner"

/**
 * This hook returns a function that allows the user to trade a certain amount of coins.
 * It has three internal functions:
 *   - The getQuote function uses the ReadQuoter from doppler to quote the amount of coins that are going to be purchased
 *   - The sellCoins function sends the transaction to make the trade. To do that it:
 *       - Gets the right nonce from the DOPPLER_V3_PERMIT2_ADDRESS
 *       - Asks the user to sign the permit to spend its coins
 *       - Creates the commands to swap the coins and return the unused ETH to the user
 *       - Attempts to write the contract that makes the coins swap
 *   - The tradeCoins functions verifies that all the necessary conditions for the swap are met and creates a loading toast
 *     when the transaction is sent. It uses the previous functions
 * @param poolAddress: The coin's pool address
 * @returns tradeCoins function
 */
export function useCoinsTrade(poolAddress: string) {
  const poolKey = usePoolKey(poolAddress)
  const account = useAccount()
  const { data: walletClient } = useWalletClient(account)
  const publicClient = usePublicClient({ chainId: CHAIN_ID })

  async function getQuote(amount: bigint) {
    if (poolKey) {
      const drift = getDrift(CHAIN_ID, walletClient)
      const quoter = new ReadQuoter(
        DOPPLER_V4_ADDRESSES[CHAIN_ID].v4Quoter,
        // @ts-ignore
        drift
      )

      const quoteConfig = {
        poolKey: poolKey,
        zeroForOne: false,
        exactAmount: amount,
        hookData: "0x" as `0x${string}`,
      }
      const quote = await quoter.quoteExactInputV4(quoteConfig)
      return quote
    }
  }

  async function sellCoins(amount: bigint) {
    if (!poolKey || !walletClient || !account.address || !publicClient)
      throw new Error("Missing values for swap")

    const [block, allowance] = await Promise.all([
      publicClient.getBlock(),
      publicClient.readContract({
        address: DOPPLER_V3_PERMIT2_ADDRESS,
        abi: permit2Abi,
        functionName: "allowance",
        args: [
          account.address,
          poolKey.currency1,
          DOPPLER_V4_ADDRESSES[CHAIN_ID].universalRouter,
        ],
      }),
    ])
    const permit = {
      details: {
        token: poolKey.currency1,
        amount,
        expiration: 0n,
        nonce: BigInt(allowance[2]),
      },
      spender: DOPPLER_V4_ADDRESSES[CHAIN_ID].universalRouter,
      sigDeadline: block.timestamp + 3600n,
    }
    const signature = await signPermit(
      permit,
      walletClient,
      CHAIN_ID,
      DOPPLER_V3_PERMIT2_ADDRESS
    )

    if (!signature) throw new Error("Missing signature")

    const [actions, params] = new V4ActionBuilder()
      .addSwapExactInSingle(poolKey, false, amount, 0n, "0x")
      .addAction(V4ActionType.SETTLE_ALL, [poolKey.currency1, maxUint256])
      .addAction(V4ActionType.TAKE_ALL, [poolKey.currency0, 0])
      .build()

    const [commands, inputs] = new CommandBuilder()
      .addPermit2Permit(permit, signature)
      .addV4Swap(actions, params)
      .addCommand(CommandType.SWEEP, [poolKey.currency0, account.address, 0n])
      .build()

    const txHash = await walletClient.writeContract({
      address: DOPPLER_V4_ADDRESSES[CHAIN_ID].universalRouter,
      abi: universalRouterAbi,
      functionName: "execute",
      args: [commands, inputs],
      value: 0n,
    })

    return txHash
  }

  async function tradeCoins(amount: bigint) {
    if (!account.address || !publicClient)
      throw new Error("Missing values for trading")

    if (account.chainId !== CHAIN_ID) throw new Error("Wrong chain")

    const quote = await getQuote(amount)

    if (!quote) throw new Error("Missing quote")

    const balance = await publicClient.getBalance({ address: account.address })
    const total = 2n * quote.gasEstimate

    if (balance < total)
      throw new Error("Not enough balance for this transaction")

    const txHash = await sellCoins(amount)
    const toastId = toast.loading("Processing your transaction")
    const receipt = await publicClient.waitForTransactionReceipt({
      hash: txHash,
    })

    if (receipt.status === "reverted") throw new Error("Transaction failed")

    return { txHash, quote: quote.amountOut, toastId }
  }

  return tradeCoins
}
